import { ApplicationConfig, importProvidersFrom, inject, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import {
  MsalService,
  MsalGuard,
  MsalBroadcastService,
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
  MsalInterceptorConfiguration,
  MsalInterceptor,
} from '@azure/msal-angular';
import { IPublicClientApplication, PublicClientApplication, InteractionType, LogLevel } from '@azure/msal-browser';
import { LoggerModule, NGXLogger } from 'ngx-logger';
import { provideIcons } from '@ng-icons/core';
import { heroCubeSolid, heroShieldExclamationSolid, heroDocumentCheckSolid } from '@ng-icons/heroicons/solid';
import { API_HOST, provideOnrsrUi } from '@onrsr/ui/core';
import { MENU_ITEMS, LAYOUT_BRANDING, LAYOUT_AUTH_SERVICE, LAYOUT_PROFILE_SERVICE } from '@onrsr/ui/layout';
import type { MenuItem } from '@onrsr/ui/layout';

import { routes } from './app.routes';
import { msalConfig, loginRequest } from './core/auth/config/auth-config';
import { environment } from '../environments/environment';
import { AuthService } from './core/auth/services/auth.service';
import { ProfileService } from './core/services/profile.service';

/**
 * Sidenav menu configuration (consumed by @onrsr/ui/layout via MENU_ITEMS).
 */
const menuItems: MenuItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: 'heroChartBarSolid', route: '/dashboard' },
  { id: 'users', label: 'Users', icon: 'heroUsersSolid', route: '/users' },
  { id: 'products', label: 'Products', icon: 'heroCubeSolid', route: '/products' },
  { id: 'roles', label: 'Roles', icon: 'heroShieldExclamationSolid', route: '/roles' },
  { id: 'audit', label: 'Audit Logs', icon: 'heroDocumentCheckSolid', route: '/audit' },
  { id: 'settings', label: 'Settings', icon: 'heroCog8ToothSolid', route: '/settings' },
];

/**
 * Factory function to create MSAL instance
 */
export function MSALInstanceFactory(): IPublicClientApplication {
  const logger = inject(NGXLogger);

  msalConfig!.system!.loggerOptions!.loggerCallback = (level: LogLevel, message: string, containsPii: boolean) => {
    if (containsPii) {
      return;
    }

    switch (level) {
      case LogLevel.Error:
        logger.error(message);
        return;
      case LogLevel.Info:
        logger.info(message);
        return;
      case LogLevel.Verbose:
        logger.debug(message);
        return;
      case LogLevel.Warning:
        logger.warn(message);
        return;
    }
  };

  return new PublicClientApplication(msalConfig);
}

/**
 * Factory function to create MSAL Guard configuration
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: loginRequest,
  };
}

/**
 * Factory function to create MSAL Interceptor configuration
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, string[] | null>([
    ['https://graph.microsoft.com/v1.0/', ['user.read', 'profile']],
    [`${environment.apiHost}/*`, [`api://${environment.azure.clientId}/access_as_user`]],
  ]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    ...provideOnrsrUi(),
    importProvidersFrom(
      LoggerModule.forRoot({
        serverLoggingUrl: environment.logging.apiEndpoint,
        level: environment.logging.level,
        serverLogLevel: environment.logging.serverLogLevel,
      })
    ),
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    {
      provide: HTTP_INTERCEPTORS, // Provides as HTTP Interceptor
      useClass: MsalInterceptor,
      multi: true,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    { provide: API_HOST, useValue: environment.apiHost },
    // ---- @onrsr/ui/layout wiring ----
    // Register the custom menu icons not bundled in the library sidenav.
    provideIcons({ heroCubeSolid, heroShieldExclamationSolid, heroDocumentCheckSolid }),
    { provide: MENU_ITEMS, useValue: menuItems },
    { provide: LAYOUT_BRANDING, useValue: { logoUrl: 'tracpass.png', title: 'TRACPASS' } },
    { provide: LAYOUT_AUTH_SERVICE, useExisting: AuthService },
    { provide: LAYOUT_PROFILE_SERVICE, useExisting: ProfileService },
  ],
};
